(function (root) {
  'use strict';

  function plan(cart, config, now) {
    if (!cart || !Array.isArray(cart.items)) throw new Error('INVALID_CART');
    var gifts = cart.items.filter(function (item) { return String(item.variant_id) === String(config.variantId); });
    var retiredIds = (config.retiredVariantIds || []).map(String).filter(function (id) { return id !== String(config.variantId); });
    var retired = cart.items.filter(function (item) { return retiredIds.indexOf(String(item.variant_id)) !== -1; });
    var retireChanges = retired.map(function (item) { return { id: item.key, quantity: 0 }; });
    var subtotal = cart.items.reduce(function (sum, item) {
      if (String(item.variant_id) === String(config.variantId) || retiredIds.indexOf(String(item.variant_id)) !== -1) return sum;
      if (!Number.isSafeInteger(item.original_line_price) || item.original_line_price < 0) throw new Error('INVALID_PRICE');
      return sum + item.original_line_price;
    }, 0);
    var testing = config.testActive === true && Number.isFinite(config.testUntil) && now < config.testUntil;
    var active = config.enabled && (testing || now >= config.start && now < config.end);
    var eligible = active && cart.currency === config.currency && subtotal >= config.minimum;
    var invalidPrice = gifts.some(function (item) { return item.final_line_price !== 0 || item.original_line_price !== 0; });
    if (!eligible || invalidPrice) {
      return { subtotal: subtotal, active: active, eligible: eligible, invalidPrice: invalidPrice,
        changes: retireChanges.concat(gifts.map(function (item) { return { id: item.key, quantity: 0 }; })), add: false };
    }
    var changes = retireChanges.concat(gifts.slice(1).map(function (item) { return { id: item.key, quantity: 0 }; }));
    if (gifts.length) {
      var gift = gifts[0];
      if (gift.quantity !== 1 || !gift.properties || gift.properties._msiv_gift_campaign !== config.campaign) {
        changes.push({ id: gift.key, quantity: 1, properties: Object.assign({}, gift.properties || {}, { _msiv_gift_campaign: config.campaign }) });
      }
    }
    return { subtotal: subtotal, active: active, eligible: eligible, invalidPrice: false, changes: changes, add: gifts.length === 0 };
  }

  function createController(config, io) {
    var tail = Promise.resolve();
    var running = null;
    var dirty = false;
    var unavailable = false;
    var invalidPrice = false;
    var lastCart = null;

    function enqueue(operation) {
      var request = tail.then(operation);
      tail = request.catch(function () {});
      return request;
    }

    function external(operation) {
      dirty = true;
      return enqueue(operation).then(function (response) {
        dirty = true;
        io.schedule();
        return response;
      }, function (error) {
        dirty = true;
        io.schedule();
        throw error;
      });
    }

    async function reconcile() {
      var sections = null;
      var changed = false;
      for (var pass = 0; pass < 20; pass += 1) {
        dirty = false;
        await tail;
        lastCart = await io.read();
        var next = plan(lastCart, config, io.now());
        if (next.invalidPrice) invalidPrice = true;
        if (next.changes.length) {
          var change = await enqueue(function () { return io.mutate('change', next.changes[0]); });
          sections = change.sections || sections;
          changed = true;
          continue;
        }
        if (next.add && !unavailable && !invalidPrice) {
          try {
            var added = await enqueue(function () {
              return io.mutate('add', { items: [{ id: config.variantId, quantity: 1,
                properties: { _msiv_gift_campaign: config.campaign } }] });
            });
            sections = added.sections || sections;
            changed = true;
            continue;
          } catch (error) {
            if (error.status !== 422 && error.status !== 404) throw error;
            unavailable = true;
            io.status(config.messages.unavailable);
            continue;
          }
        }
        if (dirty) continue;
        if (changed) await io.render(lastCart, sections);
        if (dirty) continue;
        io.active(next.active);
        if (invalidPrice) io.status(config.messages.invalid_price);
        return lastCart;
      }
      throw new Error('CART_BUSY');
    }

    function sync() {
      dirty = true;
      if (!running) {
        running = reconcile().finally(function () { running = null; });
      }
      return running;
    }

    return { sync: sync, external: external, state: function () { return { unavailable: unavailable, invalidPrice: invalidPrice, cart: lastCart }; } };
  }

  if (typeof module === 'object' && module.exports) module.exports = { plan: plan, createController: createController };
  if (!root || !root.document || root.MSIVGift) return;
  var document = root.document;
  var element = document.getElementById('MSIVGiftConfig');
  if (!element) return;
  var config;
  try { config = JSON.parse(element.textContent); } catch (error) { return; }
  if (!config.enabled || !Number.isSafeInteger(config.variantId) || config.variantId <= 0) return;

  var rawFetch = root.fetch.bind(root);
  var clockAnchor = config.serverNow;
  var clockMark = root.performance.now();
  var timer;
  var checkoutRunning = false;
  var resuming = false;
  var lastQuantityChange = -Infinity;
  function now() { return clockAnchor + root.performance.now() - clockMark; }
  function active() { return config.testActive === true && Number.isFinite(config.testUntil) && now() < config.testUntil || now() >= config.start && now() < config.end; }
  function syncActive() { document.documentElement.classList.toggle('msiv-gift-active', active()); }
  function status(message) {
    var live = document.getElementById('MSIVGiftStatus');
    if (live) { live.textContent = message; live.hidden = !message; }
  }
  function sections() {
    var ids = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections: ids } }));
    return Array.from(new Set(ids)).slice(0, 5);
  }
  async function request(path, body) {
    var abort = new AbortController();
    var timeout = root.setTimeout(function () { abort.abort(); }, 12000);
    try {
      var response = await rawFetch(config.root + path, body ? {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'same-origin',
        body: JSON.stringify(body), signal: abort.signal
      } : { credentials: 'same-origin', cache: 'no-store', signal: abort.signal });
      var serverDate = Date.parse(response.headers.get('Date'));
      if (Number.isFinite(serverDate)) { clockAnchor = serverDate; clockMark = root.performance.now(); }
      syncActive();
      armBoundary();
      var data = await response.json();
      if (!response.ok || data.status || data.errors) {
        var error = new Error(data.description || data.message || 'CART_ERROR');
        error.status = response.status;
        throw error;
      }
      return data;
    } finally { root.clearTimeout(timeout); }
  }
  async function render(cart) {
    var ids = sections();
    var html = {};
    if (ids.length) {
      var url = new URL(root.location.href);
      url.search = '';
      url.searchParams.set('sections', ids.join(','));
      var response = await rawFetch(url.href, { credentials: 'same-origin', cache: 'no-store' });
      if (!response.ok) throw new Error('CART_RENDER_ERROR');
      html = await response.json();
    }
    if (ids.some(function (id) { return typeof html[id] !== 'string'; })) throw new Error('CART_RENDER_ERROR');
    if (cart.item_count > 0) {
      document.querySelectorAll('main-cart, cart-drawer').forEach(function (node) {
        var id = node.getAttribute('data-section-id');
        if (!id || !html[id]) return;
        var expected = (node.tagName.toLowerCase() === 'main-cart' ? 'MainCart-' : 'MiniCart-') + id;
        if (!new DOMParser().parseFromString(html[id], 'text/html').getElementById(expected)) throw new Error('CART_RENDER_ERROR');
      });
    }
    if (root.theme && root.theme.pubsub) {
      cart.sections = html;
      rendering = true;
      try { root.theme.pubsub.publish(root.theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'msiv-free-gift', cart: cart }); }
      finally { rendering = false; }
    }
  }
  function schedule() {
    root.clearTimeout(timer);
    timer = root.setTimeout(function () {
      controller.sync().catch(function () { status(config.messages.error); });
    }, 60);
  }
  var controller = createController(config, {
    now: now, read: function () { return request('cart.js'); }, schedule: schedule, status: status, active: syncActive,
    mutate: function (type, payload) {
      return request('cart/' + type + '.js', Object.assign({}, payload, { sections: sections(), sections_url: root.location.pathname }));
    }, render: render
  });
  root.MSIVGift = { sync: controller.sync, state: controller.state };
  root.fetch = function (input, options) {
    var url;
    try { url = new URL(typeof input === 'string' || input instanceof URL ? input : input.url, root.location.href); } catch (error) { return rawFetch(input, options); }
    var method = String(options && options.method || input && input.method || 'GET').toUpperCase();
    if (url.origin === root.location.origin && method === 'POST' && /\/cart\/(add|change|update|clear)(?:\.js)?\/?$/.test(url.pathname)) {
      return controller.external(function () { return rawFetch(input, options); });
    }
    return rawFetch(input, options);
  };
  function setBusy(busy) {
    document.documentElement.classList.toggle('msiv-gift-checkout-pending', busy);
    document.querySelectorAll('button[name="checkout"]').forEach(function (button) {
      if (busy) button.setAttribute('aria-busy', 'true');
      else button.removeAttribute('aria-busy');
    });
  }
  function isCartForm(form) { return form && (form.id === 'cart' || form.matches('cart-drawer form.drawer__footer-buttons')); }
  async function proceed(kind) {
    if (checkoutRunning) return;
    checkoutRunning = true;
    setBusy(true);
    status('');
    try {
      if (document.activeElement && document.activeElement.closest('cart-items')) document.activeElement.blur();
      var delay = Math.max(0, 350 - (root.performance.now() - lastQuantityChange));
      if (delay) await new Promise(function (resolve) { root.setTimeout(resolve, delay); });
      await controller.sync();
      var form = kind === 'main' ? document.getElementById('cart') : document.querySelector('cart-drawer form.drawer__footer-buttons');
      var button = form && (kind === 'main' ? document.querySelector('button[name="checkout"][form="cart"]') : form.querySelector('button[name="checkout"]'));
      if (!form || !button) throw new Error('CHECKOUT_FORM_MISSING');
      resuming = true;
      try { form.requestSubmit(button); } finally { resuming = false; }
    } catch (error) {
      status(config.messages.error);
    } finally { checkoutRunning = false; setBusy(false); }
  }
  document.addEventListener('submit', function (event) {
    if (resuming || !isCartForm(event.target) || !event.submitter || event.submitter.name !== 'checkout') return;
    event.preventDefault();
    event.stopImmediatePropagation();
    proceed(event.target.id === 'cart' ? 'main' : 'drawer');
  }, true);
  document.addEventListener('click', function (event) {
    var anchor = event.target.closest && event.target.closest('a[href]');
    if (!anchor || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    var url = new URL(anchor.href, root.location.href);
    if (url.origin !== root.location.origin || !/\/(?:checkout|checkouts)(?:\/|$)/.test(url.pathname)) return;
    if (!active()) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (checkoutRunning) return;
    checkoutRunning = true;
    controller.sync().then(function () { root.location.assign(anchor.href); }).catch(function () { status(config.messages.error); }).finally(function () { checkoutRunning = false; });
  }, true);
  document.addEventListener('cart:updated', function () {
    if (rendering) return;
    schedule();
  });
  document.addEventListener('change', function (event) {
    if (event.target.matches('cart-items input[name="updates[]"]')) lastQuantityChange = root.performance.now();
  }, true);
  var rendering = false;
  root.addEventListener('pageshow', function () { setBusy(false); schedule(); });
  document.addEventListener('visibilitychange', function () { if (!document.hidden) schedule(); });
  var boundary;
  function armBoundary() {
    root.clearTimeout(boundary);
    var target = now() < config.start ? config.start : config.end;
    if (config.testActive && config.testUntil > now()) target = Math.min(target, config.testUntil);
    if (now() < config.end) boundary = root.setTimeout(function () { syncActive(); schedule(); armBoundary(); }, Math.max(100, Math.min(target - now() + 100, 2147483647)));
  }
  syncActive();
  armBoundary();
  schedule();
})(typeof window === 'undefined' ? null : window);
